import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { VenueTournamentReport } from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';

function todayInTaipei() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (key: string) => parts.find((p) => p.type === key)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}
export default function VenueTournamentReportTab() {
  const { t } = useTranslation('admin');
  const [from, setFrom] = useState(() => `${todayInTaipei().slice(0, 7)}-01`);
  const [to, setTo] = useState(todayInTaipei);
  const actor = useAuthStore((s) => s.user?.id),
    token = useAuthStore((s) => s.accessToken),
    revision = useAuthStore((s) => s.sessionRevision);
  const valid = !!from && !!to && from <= to;
  const query = useQuery({
    queryKey: ['venue-tournament-report-v2', actor, sessionVenueKey(token), revision, from, to],
    queryFn: async ({ signal }) => {
      const result = await api.get<VenueTournamentReport>('/venues/reports/tournaments', {
        signal,
        params: { from, to },
      });
      if (
        result?.contractVersion !== 'venue-tournament-report-v2' ||
        !result.range ||
        !Array.isArray(result.venues)
      )
        throw Error('UNSUPPORTED_VENUE_REPORT');
      return result;
    },
    enabled: valid,
    retry: false,
  });
  const report =
    valid && !query.isError && query.data?.range.from === from && query.data.range.to === to
      ? query.data
      : undefined;
  const cell = 'px-4 py-3 whitespace-nowrap';
  const number = `${cell} text-right tabular-nums`;
  return (
    <section className="space-y-4" aria-label={t('venue.tournamentReport')}>
      <h1 className="admin-page-title">{t('venue.tournamentReport')}</h1>
      <p>{t('venue.operationalNotice')}</p>
      <p className="text-sm text-base-content/60">{t('venue.dateBasis')}</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          {t('venue.from')}
          <input
            type="date"
            className="input input-bordered"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          {t('venue.to')}
          <input
            type="date"
            className="input input-bordered"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="admin-button"
          disabled={!valid || query.isFetching}
          onClick={() => void query.refetch()}
        >
          {t('venue.retryReport')}
        </button>
      </div>
      {!valid && (
        <p role="alert" className="text-error">
          {t('venue.invalidReportRange')}
        </p>
      )}
      {query.isFetching && <p role="status">{t('venue.reportLoading')}</p>}
      {query.isError && (
        <p role="alert" className="text-error">
          {t('venue.reportFailed')}
        </p>
      )}
      {report && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              [t('venue.totalTournaments'), report.grandTotal.tournamentCount],
              [t('venue.operationalPrizePool'), `${report.grandTotal.operationalPrizePool} TWD`],
              [t('venue.entries'), report.grandTotal.entries],
            ].map(([label, value]) => (
              <div key={label} className="admin-panel bg-base-200">
                <div className="text-sm">{label}</div>
                <div className="text-2xl font-semibold tabular-nums">{value}</div>
              </div>
            ))}
          </div>
          {report.grandTotal.warnings.length > 0 && (
            <ul className="text-warning" aria-label={t('venue.reportWarnings')}>
              {report.grandTotal.warnings.map((w) => (
                <li key={w}>{t(`venue.reportWarning.${w}`)}</li>
              ))}
            </ul>
          )}
          <div
            className="overflow-x-auto"
            tabIndex={0}
            role="region"
            aria-label={t('venue.tournamentReport')}
          >
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr>
                  <th className={cell}>{t('venue.venueName')}</th>
                  <th className={number}>{t('venue.tournamentCount')}</th>
                  <th className={number}>{t('venue.entries')}</th>
                  <th className={number}>{t('venue.operationalPrizePool')} (TWD)</th>
                  <th className={number}>{t('venue.avgConfiguredBuyin')} (TWD)</th>
                  <th className={cell}>{t('venue.reportWarnings')}</th>
                </tr>
              </thead>
              <tbody>
                {report.venues.map((v) => (
                  <tr key={v.venueId} className="border-t border-base-300">
                    <th className={cell}>
                      {v.name} <span className="text-base-content/60">{v.code}</span>
                    </th>
                    <td className={number}>{v.tournamentCount}</td>
                    <td className={number}>{v.entries}</td>
                    <td className={number}>{v.operationalPrizePool}</td>
                    <td className={number}>{v.avgConfiguredBuyin ?? '—'}</td>
                    <td className="px-4 py-3">
                      {v.warnings.map((w) => (
                        <p key={w} className="text-warning">
                          {t(`venue.reportWarning.${w}`)}
                          {w === 'LEGACY_DATE_FALLBACK'
                            ? ` (${v.legacyDateFallbackCount})`
                            : w === 'UNDATED_TOURNAMENTS_EXCLUDED'
                              ? ` (${v.undatedExcludedCount})`
                              : ''}
                        </p>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
